import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

interface SplitHeroProps {
  eyebrow?: string;
  title: ReactNode;
  subtitle: string;
  graphic: ReactNode;
  primaryLabel?: string;
  primaryTo?: string;
  secondaryLabel?: string;
  secondaryTo?: string;
  stat?: { value: string; label: string };
}

const SplitHero = ({
  eyebrow,
  title,
  subtitle,
  graphic,
  primaryLabel,
  primaryTo,
  secondaryLabel,
  secondaryTo,
  stat,
}: SplitHeroProps) => {
  return (
    <section className="bg-gradient-to-b from-primary-50/50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-20 md:pt-20 md:pb-28">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            {eyebrow && (
              <div className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-600 mb-4">{eyebrow}</div>
            )}
            <h1 className="text-4xl md:text-5xl font-serif font-medium text-slate-900 leading-[1.1] mb-6">
              {title}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-9 max-w-lg">{subtitle}</p>

            {(primaryLabel || secondaryLabel) && (
              <div className="flex flex-wrap items-center gap-4">
                {primaryLabel && primaryTo && (
                  <Link
                    to={primaryTo}
                    className="inline-flex items-center gap-2 bg-primary-800 hover:bg-primary-900 text-white font-semibold px-6 py-3.5 transition-colors"
                  >
                    {primaryLabel}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {secondaryLabel && secondaryTo && (
                  <Link
                    to={secondaryTo}
                    className="inline-flex items-center gap-2 border border-slate-300 hover:border-primary-400 text-slate-700 font-medium px-6 py-3.5 transition-colors"
                  >
                    {secondaryLabel}
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="relative">
            <div className="relative overflow-hidden border-l-4 border-gold-500 shadow-[0_20px_60px_rgba(15,23,42,0.12)] aspect-[4/5] md:aspect-[5/6]">
              {graphic}
            </div>

            {stat && (
              <div className="absolute -bottom-6 -left-6 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.14)] border border-slate-100 border-t-2 border-t-gold-500 px-5 py-4 hidden sm:block">
                <div className="text-2xl font-serif font-semibold text-primary-800">{stat.value}</div>
                <div className="text-xs text-slate-500 mt-0.5">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SplitHero;
